import type { HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import {
  extractItems,
  extractTotal,
  isRecord,
  readCount,
  readFirstText,
  readText,
} from "@/core/utils";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import { DEFAULT_USERS_SORT, parseUsersSort, USER_STATUS_PAYLOAD } from "./users.types";
import type {
  ListUsersParams,
  UpdateUserRoleParams,
  UpdateUserStatusParams,
  User,
  UserFilterOption,
  UserRole,
  UsersPage,
  UsersSort,
  UsersSortField,
  UserStatus,
} from "./users.types";

export const USER_PATHS = {
  LIST: "/api/plataforma/usuarios",
} as const;

/** `PATCH /api/plataforma/usuarios/{id}/status` — alteração do estado de acesso. */
export function buildUserStatusPath(id: string): string {
  return `${USER_PATHS.LIST}/${encodeURIComponent(id)}/status`;
}

const COLLECTION_KEYS = ["usuarios"];

/** Recurso do vínculo de perfil do usuário, conforme o contrato da história pai. */
export function buildUserRolePath(id: string): string {
  return `${USER_PATHS.LIST}/${encodeURIComponent(id)}/perfil`;
}

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function readStatus(raw: Record<string, unknown>): User["status"] {
  const status = readFirstText(raw, "status", "situacao").trim().toLowerCase();
  if (status) return status.startsWith("inativ") || status === "inactive" ? "inativo" : "ativo";

  const flag = raw.ativo ?? raw.active;
  if (typeof flag === "boolean") return flag ? "ativo" : "inativo";

  return "ativo";
}

/** Distingue "o serviço informou o status" de "não veio status", que `readStatus` não faz. */
function readReturnedStatus(raw: unknown): UserStatus | null {
  if (!isRecord(raw)) return null;

  const informed =
    readFirstText(raw, "status", "situacao").trim() !== "" ||
    typeof (raw.ativo ?? raw.active) === "boolean";

  return informed ? readStatus(raw) : null;
}

/** O serviço envia o vínculo aninhado (`role`/`perfil`/`papel`) ou em campos planos. */
function readPapel(raw: Record<string, unknown>): UserRole {
  const nested = [raw.papel, raw.perfil, raw.role].find(isRecord);

  return {
    papelId: nested
      ? readFirstText(nested, "id")
      : readFirstText(raw, "papelId", "papel_id", "perfilId", "perfil_id", "roleId"),
    papel: nested
      ? readFirstText(nested, "nome", "name")
      : readFirstText(raw, "papelNome", "papel_nome", "papel", "perfilNome", "roleName"),
  };
}

/** Desembrulha o envelope do BFF (`{ success, data }`) até chegar ao recurso. */
function extractResource(payload: unknown): unknown {
  if (!isRecord(payload)) return payload;

  for (const key of ["data", "usuario"]) {
    const nested = payload[key];
    if (isRecord(nested)) return extractResource(nested);
  }

  return payload;
}

function toUser(raw: unknown): User | null {
  if (!isRecord(raw)) return null;

  const email = readFirstText(raw, "email", "mail");
  const id = readText(raw.id) || readFirstText(raw, "usuarioId", "userId") || email;
  if (!id) return null;

  return {
    id,
    nome: readFirstText(raw, "nome", "name"),
    email,
    celular: readFirstText(
      raw,
      "celular",
      "telefone",
      "phone",
      "mobilePhone",
      "telefoneCelular",
      "phoneNumber",
    ),
    time: readFirstText(raw, "time", "team", "area"),
    ...readPapel(raw),
    status: readStatus(raw),
    fotoUrl: readFirstText(raw, "fotoUrl", "foto_url", "foto", "photoUrl", "avatarUrl"),
    ultimaAtividade: readFirstText(
      raw,
      "ultimaAtividade",
      "ultima_atividade",
      "ultimoAcesso",
      "lastActivity",
      "lastActivityAt",
    ),
  };
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, "pt-BR", { sensitivity: "base" });
}

/** Datas inválidas caem na comparação textual em vez de virar `NaN`. */
function compareDates(a: string, b: string): number {
  const left = Date.parse(a);
  const right = Date.parse(b);

  if (Number.isNaN(left) || Number.isNaN(right)) return compareText(a, b);

  return left === right ? 0 : left < right ? -1 : 1;
}

const SORT_VALUES: Record<UsersSortField, (user: User) => string> = {
  nome: (user) => user.nome,
  email: (user) => user.email,
  time: (user) => user.time,
  papel: (user) => user.papel,
  status: (user) => user.status,
  ultimaAtividade: (user) => user.ultimaAtividade,
};

/** Mantém a ordenação estável na tela mesmo se o serviço ignorar `ordenacao`. */
function sortUsers(users: User[], ordenacao: UsersSort): User[] {
  const { field, direction } = parseUsersSort(ordenacao);
  const readValue = SORT_VALUES[field];
  const factor = direction === "desc" ? -1 : 1;

  return [...users].sort((a, b) => {
    const left = readValue(a);
    const right = readValue(b);

    // Células sem valor ("Sem perfil", sem atividade) ficam no fim nas duas direções.
    if (!left || !right) return left ? -1 : right ? 1 : 0;

    const compared =
      field === "ultimaAtividade" ? compareDates(left, right) : compareText(left, right);

    return compared * factor || compareText(a.nome, b.nome);
  });
}

function toFilterOption(raw: unknown): UserFilterOption | null {
  if (typeof raw === "string") {
    const label = raw.trim();
    return label ? { label, value: label } : null;
  }

  if (!isRecord(raw)) return null;

  const label = readFirstText(raw, "nome", "name", "label", "descricao");
  const value = readFirstText(raw, "id", "value", "codigo") || label;

  return label && value ? { label, value } : null;
}

function sortOptions(options: UserFilterOption[]): UserFilterOption[] {
  return options.sort((a, b) => compareText(a.label, b.label));
}

function dedupeOptions(options: (UserFilterOption | null)[]): UserFilterOption[] {
  const byValue = new Map<string, UserFilterOption>();

  for (const option of options) {
    if (option && !byValue.has(option.value)) byValue.set(option.value, option);
  }

  return sortOptions([...byValue.values()]);
}

/**
 * Ainda não há endpoint dedicado para as opções de Time e Perfil: quando o
 * serviço envia o bloco `filtros`, ele é a fonte; caso contrário as opções são
 * derivadas dos registros retornados e acumuladas pelo hook de listagem.
 */
function extractFilterOptions(payload: unknown, key: string): UserFilterOption[] {
  if (!isRecord(payload)) return [];

  const meta = isRecord(payload.meta) ? payload.meta : undefined;
  const filtros = isRecord(payload.filtros)
    ? payload.filtros
    : isRecord(meta?.filtros)
      ? meta.filtros
      : undefined;

  if (filtros && Array.isArray(filtros[key])) {
    return dedupeOptions((filtros[key] as unknown[]).map(toFilterOption));
  }

  return isRecord(payload.data) ? extractFilterOptions(payload.data, key) : [];
}

const SEM_PAPEL_KEYS = [
  "totalSemPerfil",
  "total_sem_perfil",
  "semPerfil",
  "usuariosSemPerfil",
  "totalSemPapel",
  "total_sem_papel",
  "semPapel",
  "usuariosSemPapel",
];

/**
 * Quantidade de usuários sem perfil definido. Quando o serviço informa o total da
 * base filtrada ele é a fonte; caso contrário resta contar os registros da página
 * recebida, que é o que a tela tem em mãos.
 */
function readSemPapel(payload: unknown, users: User[]): number {
  const fromPayload = extractSemPapel(payload);
  if (fromPayload !== null) return fromPayload;

  return users.filter((user) => !user.papel).length;
}

function extractSemPapel(payload: unknown): number | null {
  if (!isRecord(payload)) return null;

  const meta = isRecord(payload.meta) ? payload.meta : undefined;

  for (const source of [meta, payload]) {
    if (!source) continue;

    for (const key of SEM_PAPEL_KEYS) {
      const count = readCount(source[key]);
      if (count !== null) return count;
    }
  }

  return isRecord(payload.data) ? extractSemPapel(payload.data) : null;
}

function timesFromUsers(users: User[]): UserFilterOption[] {
  return dedupeOptions(
    users.map((user) => (user.time ? { label: user.time, value: user.time } : null)),
  );
}

function papeisFromUsers(users: User[]): UserFilterOption[] {
  return dedupeOptions(
    users.map((user) =>
      user.papel && user.papelId ? { label: user.papel, value: user.papelId } : null,
    ),
  );
}

export async function listUsers({
  pesquisa,
  time,
  papelId,
  page,
  pageSize,
  ordenacao = DEFAULT_USERS_SORT,
  signal,
}: ListUsersParams = {}): Promise<UsersPage> {
  const term = pesquisa?.trim();

  const params: Record<string, string | number> = {};
  if (term) params.pesquisa = term;
  if (time) params.time = time;
  if (papelId) params.perfilId = papelId;
  if (page) params.page = page;
  if (pageSize) params.pageSize = pageSize;
  params.ordenacao = ordenacao;

  const config: HttpRequestConfig = { signal, params };

  const response = await getHttpClient().get<unknown>(USER_PATHS.LIST, config);

  const users = sortUsers(
    extractItems(response.data, COLLECTION_KEYS)
      .map(toUser)
      .filter((user): user is User => user !== null),
    ordenacao,
  );

  const times = extractFilterOptions(response.data, "times");
  const fromPerfis = extractFilterOptions(response.data, "perfis");
  const papeis = fromPerfis.length > 0 ? fromPerfis : extractFilterOptions(response.data, "papeis");

  return {
    users,
    total: extractTotal(response.data) ?? users.length,
    semPapel: readSemPapel(response.data, users),
    times: times.length > 0 ? times : timesFromUsers(users),
    papeis: papeis.length > 0 ? papeis : papeisFromUsers(users),
  };
}

/**
 * Altera o estado de acesso do usuário (RN01/RN03). O corpo carrega apenas
 * `status`, no formato `Ativo | Inativo` aceito pelo serviço; o perfil associado
 * não é enviado e permanece como está (RN05).
 *
 * Retorna o status confirmado pelo serviço ou `null` quando a resposta não traz
 * corpo utilizável (ex.: 204), cabendo ao chamador manter o que foi enviado.
 */
export async function updateUserStatus({
  id,
  status,
  signal,
}: UpdateUserStatusParams): Promise<UserStatus | null> {
  const body = { status: USER_STATUS_PAYLOAD[status] };

  const response = await getHttpClient().patch<unknown>(buildUserStatusPath(id), body, { signal });

  return readReturnedStatus(extractResource(response.data));
}

/**
 * Atribui ou altera o perfil do usuário. Devolve o vínculo confirmado pelo
 * serviço ou `null` quando a resposta não traz corpo utilizável (ex.: 204),
 * cabendo ao chamador reaproveitar o que enviou.
 */
export async function updateUserRole({
  id,
  papelId,
  signal,
}: UpdateUserRoleParams): Promise<UserRole | null> {
  const response = await getHttpClient().put<unknown>(
    buildUserRolePath(id),
    { perfilId: papelId },
    { signal },
  );

  const resource = extractResource(response.data);
  if (!isRecord(resource)) return null;

  const role = readPapel(resource);

  return role.papelId || role.papel ? role : null;
}
